/**
 * VrSummonOrb — the way to the world panel when there is no X button (XR P2 "VR UI / UX", 2026-09-28).
 *
 * Hands-only sessions (Quest hand tracking) and Vision Pro (gaze-and-pinch: the hands carry no gamepad at all) have
 * nothing to press. A small glowing orb floats low in view — a TAG-ALONG: it keeps its bearing until the head turns
 * more than `ORB.followDeg` away and only then eases back in front (never head-locked) — and a select on it (a
 * pinch, a transient pointer's gaze-and-pinch, a trigger) opens the panel. It hides while the panel or the wrist menu
 * is up, and whenever the session HAS an X button.
 */

import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { col } from './vr_draw.js';
import { ORB } from '../../lib/xr_ui_layout.js';
import { orbStep, orbPos, headingOf, rayHitSphere, faceToward, type V3 } from '../../lib/xr_ui_input.js';

/** The pick radius is larger than the drawn orb: a pinch ray from a resting hand is not a precision instrument. */
const PICK_R = ORB.radiusM * 1.8;

export class VrSummonOrb {
  readonly group = new THREE.Group();
  private readonly core: THREE.Mesh;
  private readonly ring: THREE.Mesh;
  private readonly coreMat: THREE.MeshBasicMaterial;
  private readonly ringMat: THREE.MeshBasicMaterial;
  private bearing: number | null = null;
  private t = 0;
  private theme = '';
  shows = 0;

  constructor() {
    this.coreMat = new THREE.MeshBasicMaterial({ color: 0xd9a441, transparent: true, opacity: 0.92, depthTest: false, depthWrite: false, toneMapped: false });
    this.ringMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.7, depthTest: false, depthWrite: false, toneMapped: false, side: THREE.DoubleSide });
    markMrtOverlay(this.coreMat);
    markMrtOverlay(this.ringMat);
    this.core = new THREE.Mesh(new THREE.SphereGeometry(ORB.radiusM, 20, 14), this.coreMat);
    this.ring = new THREE.Mesh(new THREE.RingGeometry(ORB.radiusM * 1.35, ORB.radiusM * 1.55, 40), this.ringMat);
    this.core.renderOrder = 998;
    this.ring.renderOrder = 998;
    this.core.frustumCulled = false;
    this.ring.frustumCulled = false;
    this.group.add(this.core, this.ring);
    this.group.name = 'vr-summon-orb';
    this.group.visible = false;
  }

  get visible(): boolean { return this.group.visible; }

  /** Per frame (rig-local head): place it lazily, or hide it. */
  update(dt: number, head: V3, headFwd: V3, want: boolean, hovered: boolean): void {
    if (!want) {
      this.group.visible = false;
      this.bearing = null;
      return;
    }
    if (!this.group.visible) this.shows++;
    const theme = document.documentElement.dataset.axTheme ?? '';
    if (theme !== this.theme) {
      this.theme = theme;
      this.coreMat.color.setStyle(col('--ax-accent', '#d9a441'));
      this.ringMat.color.setStyle(col('--ax-hi', '#ffffff'));
    }
    const hh = headingOf(headFwd);
    this.bearing = this.bearing === null ? hh : orbStep(this.bearing, hh, dt, ORB.followDeg, ORB.rate);
    const p = orbPos(head, this.bearing, ORB.distanceM, ORB.dropM);
    this.group.position.set(p.x, p.y, p.z);
    const q = faceToward(p, head);
    this.ring.quaternion.set(q.x, q.y, q.z, q.w);
    this.t += dt;
    const pulse = 1 + 0.08 * Math.sin(this.t * 2.4);
    const s = hovered ? 1.3 : pulse;
    this.core.scale.setScalar(s);
    this.ring.scale.setScalar(hovered ? 1.25 : 1);
    this.ringMat.opacity = hovered ? 1 : 0.55;
    this.group.visible = true;
  }

  center(): V3 { const p = this.group.position; return { x: p.x, y: p.y, z: p.z }; }

  hit(o: V3, d: V3): { target: string; t: number; point: V3 } | null {
    if (!this.group.visible) return null;
    const t = rayHitSphere(o, d, this.center(), PICK_R);
    return t === null ? null : { target: 'orb:', t, point: { x: o.x + d.x * t, y: o.y + d.y * t, z: o.z + d.z * t } };
  }

  snapshot(): Record<string, unknown> { return { visible: this.visible, center: this.center(), shows: this.shows, bearing: this.bearing }; }

  dispose(): void {
    this.group.removeFromParent();
    this.core.geometry.dispose();
    this.ring.geometry.dispose();
    this.coreMat.dispose();
    this.ringMat.dispose();
  }
}
